import { For, Show } from "solid-js";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FolderPlus, Plus } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";

interface AddToGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupName: string;
  ungroupedLists: { id: string; name: string; icon?: string | null }[];
  onAddExistingList: (listId: string) => void;
  onAddNewList: () => void;
}

export function AddToGroupDialog(props: AddToGroupDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader size="md">
        <div class="flex items-center gap-3">
          <div class="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
            <FolderPlus class="h-5 w-5" />
          </div>
          <DialogTitle class="text-xl font-bold">
            {(() => {
              const name = props.groupName;
              return t(msg`Add to ${name}`);
            })()}
          </DialogTitle>
        </div>
      </DialogHeader>

      <div class="p-6 space-y-6">
        <button
          type="button"
          onClick={() => {
            props.onAddNewList();
            props.onOpenChange(false);
          }}
          class="w-full flex items-center gap-3 p-4 rounded-2xl border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 transition-all group cursor-pointer"
        >
          <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
            <Plus class="h-5 w-5" />
          </div>
          <div class="font-bold text-left">{t(msg`Create new list...`)}</div>
        </button>

        <Show when={props.ungroupedLists.length > 0}>
          <div class="space-y-3">
            <div class="text-xs uppercase tracking-wider font-bold opacity-40 px-1">
              {t(msg`Existing lists`)}
            </div>
            <div class="max-h-60 overflow-y-auto pr-2 space-y-1 scrollbar-thin">
              <For each={props.ungroupedLists}>
                {(list) => (
                  <button
                    type="button"
                    onClick={() => {
                      props.onAddExistingList(list.id);
                      props.onOpenChange(false);
                    }}
                    class="w-full flex items-center gap-3 p-3 rounded-2xl hover:bg-accent transition-colors text-left group cursor-pointer"
                  >
                    <div class="text-xl h-10 w-10 flex items-center justify-center rounded-xl bg-muted group-hover:bg-card transition-colors">
                      {list.icon ?? "📋"}
                    </div>
                    <div class="font-medium">{list.name}</div>
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>

        <div class="flex justify-end pt-2">
          <Button
            variant="ghost"
            class="w-full h-11 font-bold rounded-xl"
            onClick={() => {
              props.onOpenChange(false);
            }}
          >
            {t(msg`Cancel`)}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
